import { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight, Sparkles, Globe2, ShieldCheck, ChevronDown,
} from "lucide-react";
import { IMAGES } from "@/lib/data";
import LogoWordmark from "@/components/LogoWordmark";
import ServiceModal from "@/components/ServiceModal";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import ServiceTracks from "@/components/ServiceTracks";
import ProactiveSplit from "@/components/ProactiveSplit";
import IndustriesAccordion from "@/components/IndustriesAccordion";
import WhyPartner from "@/components/WhyPartner";
import PartnerBenefitsDark from "@/components/PartnerBenefitsDark";
import BreakthroughCTA from "@/components/BreakthroughCTA";
import Seo from "@/components/Seo";

const TRUST_BADGES = [
    "GDPR Compliant",
    "NDA Protected",
    "EU Client Experience",
    "ISO-Aligned Processes",
];

export default function Home() {
    const [active, setActive] = useState(null);

    return (
        <div data-testid="home-page">
            <Seo
                path="/"
                title={null}
                description="Vittaniti Shared Solutions delivers bookkeeping services, outsourced CPA support and remote back-office teams from India to SMEs across the US and Europe — partner-led, secure and senior-reviewed."
                keywords="bookkeeping services USA, offshore accounting India, remote bookkeeping team, outsourced CPA services, extended office India, shared services Hyderabad, accounting outsourcing"
            />
            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-brand-creamLight">
                <div className="container-page pt-12 md:pt-16 pb-16 md:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <div className="lg:col-span-7 animate-fade-up">
                            <div className="inline-flex items-center gap-3 mb-7 rounded-full pl-3 pr-4 py-2 bg-white border border-brand-border shadow-sm" data-testid="hero-pill">
                                <LogoWordmark height={18} />
                                <span className="block w-px h-4 bg-brand-border" aria-hidden="true" />
                                <p className="overline text-brand-ink">Premium Offshore Partner · India</p>
                            </div>

                            <h1 className="display text-5xl sm:text-6xl lg:text-7xl text-brand-ink">
                                Build your <span className="italic text-brand-amber">global</span><br />
                                extended office<br />
                                <span className="text-brand-ink/85">from India</span><span className="brand-dot">.</span>
                            </h1>

                            <p className="mt-8 max-w-xl text-lg text-brand-ink/75 leading-relaxed">
                                Vittaniti Shared Solutions enables SMEs across the US and Europe to build a remote
                                team in India and accounting &amp; bookkeeping services for the US — combining global
                                standards, compliance and seamless team integration.
                                <span className="block mt-3 font-semibold text-brand-ink">
                                    We are more than an outsourcing vendor; we act as your extended office.
                                </span>
                            </p>

                            <div className="mt-9 space-y-3" data-testid="hero-cta-stack">
                                <div className="flex flex-wrap items-center gap-3" data-testid="hero-cta-row-tracks">
                                    <Link to="/services?track=eu" className="btn-ghost" data-testid="hero-cta-team">
                                        Build Your Remote Team
                                    </Link>
                                    <Link to="/services?track=us" className="btn-ghost" data-testid="hero-cta-accounting">
                                        Accounting &amp; Bookkeeping
                                    </Link>
                                </div>
                                <div>
                                    <Link to="/contact" className="btn-primary" data-testid="hero-cta-consult">
                                        Book a Free Consultation <ArrowUpRight size={16} />
                                    </Link>
                                </div>
                            </div>

                            <div className="mt-10 flex flex-wrap items-center gap-2.5" data-testid="hero-trust-badges">
                                {TRUST_BADGES.map((b, i) => (
                                    <span
                                        key={b}
                                        data-testid={`trust-badge-${i}`}
                                        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-white border border-brand-border text-brand-ink"
                                    >
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-amber" />
                                        {b}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="lg:col-span-5 animate-fade-in">
                            <div className="relative">
                                <div className="absolute -inset-6 rounded-[2.25rem] bg-brand-ink/5 -z-10" aria-hidden="true" />
                                <img
                                    src={IMAGES.hero}
                                    alt="Professional team in a modern, bright open workspace — Vittaniti's extended-office partnership."
                                    loading="eager"
                                    className="w-full h-[480px] md:h-[560px] object-cover rounded-[1.75rem]"
                                    style={{ boxShadow: "0 30px 70px -25px rgba(20,34,57,0.40)" }}
                                />

                                {/* Operating-from card */}
                                <div className="absolute -bottom-6 -left-6 bg-white border border-brand-border rounded-2xl p-5 shadow-lg max-w-[260px]" data-testid="hero-operating-card">
                                    <p className="overline text-brand-muted">Operating from</p>
                                    <p className="font-serif text-xl text-brand-ink mt-1">HITEC City, Hyderabad</p>
                                    <div className="mt-4 grid grid-cols-2 gap-3 text-left">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Avg. savings</p>
                                            <p className="font-serif text-2xl text-brand-amber leading-none mt-1">60%</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Status</p>
                                            <p className="flex items-center gap-1.5 font-semibold text-brand-ink text-sm mt-1">
                                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/25" />
                                                Teams active
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scroll cue */}
                    <div className="mt-14 flex flex-col items-center justify-center gap-2 text-brand-muted animate-fade-in" data-testid="scroll-cue">
                        <span className="overline">Scroll</span>
                        <ChevronDown size={18} className="animate-bounce" />
                    </div>
                </div>
            </section>

            {/* SERVICE TRACKS — second section, links to Services page */}
            <ServiceTracks
                kicker="Choose your track"
                heading={<>Two ways to work with <span className="italic text-brand-amber">Vittaniti</span>.</>}
                blurb="Pick the track that fits your business and we'll take you straight to the matching service lines."
            />

            {/* PROACTIVE SPLIT — “We Build Teams, Not Transactions” */}
            <ProactiveSplit
                image={IMAGES.deskFocus}
                kicker="Our Approach"
                headlineLead="We Build Teams,"
                headlineAccent="Not Transactions."
                blurbs={[
                    "At Vittaniti Shared Solutions, we believe that successful outsourcing goes beyond task execution. We partner with businesses across Europe and the US to build dedicated remote teams and accounting services (US) that operate as a natural extension of their organization.",
                    "Rather than providing temporary resources, we focus on creating long-term partnerships built on trust, accountability and measurable outcomes.",
                ]}
                ctaLabel="Book a Free Consultation"
            />

            {/* FEATURED INDUSTRIES (accordion teaser) */}
            <IndustriesAccordion />

            {/* TESTIMONIALS */}
            <section className="section bg-brand-creamLight border-y border-brand-border">
                <div className="container-page">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="overline mb-3">Our clients</p>
                        <h2 className="display text-4xl md:text-5xl text-brand-ink">
                            Trusted by SMEs across Europe, the US and India<span className="brand-dot">.</span>
                        </h2>
                    </div>
                    <TestimonialCarousel />
                </div>
            </section>

            {/* WHY PARTNER WITH US */}
            <WhyPartner />

            {/* PARTNER BENEFITS — dark complement */}
            <PartnerBenefitsDark />

            {/* BREAKTHROUGH CTA — closing */}
            <BreakthroughCTA
                kicker="Get started"
                heading="Your next breakthrough starts here."
                blurb="Move forward with clarity and confidence, backed by an independent partner that puts you first."
                primaryHref="/contact"
                primaryLabel="Contact us"
                secondaryHref="/pricing"
                secondaryLabel="See pricing"
            />

            <ServiceModal service={active} open={!!active} onOpenChange={(v) => !v && setActive(null)} />
        </div>
    );
}
